// lib
import React from 'react'
import { RouteComponentProps, useParams } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import PostHeaderDetailsContainer from 'src/containers/header/subheader/post_header_details'
import PostDetailsContainer from 'src/containers/post/post_details_container'
import PageTitleDetailsPage from 'src/containers/smart_components/page_title/page_title_details_page'

// constants & interfaces
import {
  DEFAULT_ROBOTS,
  DETAILS_PAGE_TITLE,
  NOINDEX_FOLLOW_ROBOTS,
  POST_ARTICLE_PUBLISHER,
  POST_OG_TYPE
} from 'src/constants/meta_tags'

// helpers
import { ContentItemType, useContentItem } from '@lopo-tech/lopo-api'
import { DETAIL_IMAGE_HEIGHT, DETAIL_IMAGE_WIDTH } from 'src/constants/image'
import { TWO_COLUMNS } from 'src/constants/layout'
import LayoutBase from 'src/layout/base/layout_base'
import { usePageTracking } from 'src/pages/hooks/hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { JsonLdSchemaHelper } from 'src/utils/json_ld_schema/json_ld_schema'
import { parseQueryString, UrlUtils } from 'src/utils/url/url'

interface IProps extends RouteComponentProps<{ id: string }> {
  contentItemType: ContentItemType;
}

const PostDetailsPage: React.FC<IProps> = ({ match, contentItemType }) => {
  const previewToken = parseQueryString(location.search, ['pt']).pt
  const { id } = useParams<{id: string}>()
  const { data, loading, error } = useContentItem({
    variables: {
      slug: id,
      type: contentItemType,
      page: 1,
      perPage: 100,
      previewToken,
      height: DETAIL_IMAGE_HEIGHT,
      width: DETAIL_IMAGE_WIDTH
    }
  })
  const contentItem = data?.contentItem

  const user = useSelectUser()

  const hasError = !!error

  const jsonLd = contentItem
    ? (contentItem?.__typename === 'Event'
      ? JsonLdSchemaHelper.detailPageEvent(contentItem)
      : JsonLdSchemaHelper.detailPagePost(contentItem))
    : ''

  const title = contentItem?.title ? DETAILS_PAGE_TITLE(contentItem.title) : undefined

  usePageTracking({ pageTitle: title, contentItem, user }, hasError)

  return (
    <>
      <MetaTags
        // article_modified_time={contentItem?.updatedAt} // TODO: check if this should be added to the api
        article_published_time={contentItem?.publishedAt}
        article_publisher={POST_ARTICLE_PUBLISHER}
        author={contentItem?.author.name}
        canonical={UrlUtils.currentUrlWithHostAndPath()}
        date={contentItem?.publishedAt}
        description={contentItem?.teaser}
        geo_placename={contentItem?.address.city ?? undefined}
        geo_position={
          contentItem
            ? `${contentItem.address.coordinates.latitude};${contentItem.address.coordinates.longitude}`
            : undefined
        }
        json_ld={jsonLd}
        // last_modified={contentItem?.updatedAt}
        og_image={contentItem?.attachments[0]?.file.previewUrl}
        og_type={POST_OG_TYPE}
        og_url={UrlUtils.currentUrlWithHostAndPathAndFilteredParams()}
        title={title}
        robots={
          contentItem?.canSeeFullContent.value
            ? DEFAULT_ROBOTS
            : NOINDEX_FOLLOW_ROBOTS
        }
      />
      <LayoutBase
        pageTitle={<PageTitleDetailsPage contentItem={contentItem} />}
        subHeader={
          <PostHeaderDetailsContainer contentItem={contentItem} match={match} />
        }
        withSidebar={false}
        enableMapToggler={false}
        isMapShown={false}
        showBanner={false}
        showStaticPagesFooter={false}
        contentWidth={TWO_COLUMNS}
        withHeaderMargin={false}
      >
        <PostDetailsContainer contentItem={contentItem} loading={loading} />
      </LayoutBase>
    </>
  )
}

export default PostDetailsPage
